import { calculateTimeUnits } from "src/utils/time/calculateTimeUnits";
import {
  ALL_TIME_LABEL,
  ALL_TIME_MIN_YEARS,
  TIME_WINDOW_ALL_TIME,
  TIME_WINDOW_DEFAULT,
} from "./TimeWindow.constants";
import { urlTimeWindow } from "./TimeWindow.urlSerialization";

export const DURATION_UNITS = ["hours", "days", "months", "years"] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];

export const NAMED_TIME_WINDOW_TOKENS = [
  "yesterday",
  "month-to-date",
  "previous-month",
  TIME_WINDOW_ALL_TIME,
] as const;
export type NamedTimeWindowToken = (typeof NAMED_TIME_WINDOW_TOKENS)[number];

export type DurationTimeWindowToken = `last-${number}-${DurationUnit}`;

/** Every value the URL/storage accepts, except absolute `~~` ranges. */
export type TimeWindowSelectionToken =
  | DurationTimeWindowToken
  | NamedTimeWindowToken;

// `n` is bounded so a hand-edited URL cannot ask for a date millennia back.
const LAST_TOKEN_RE = /^last-([1-9]\d{0,3})-(hours|days|months|years)$/;

export type ParsedTimeWindowSelection =
  | { kind: "duration"; n: number; unit: DurationUnit }
  | { kind: "named"; token: NamedTimeWindowToken }
  | { kind: "range"; start: Date; end: Date };

export const serializeDurationToken = (n: number, unit: DurationUnit): string => {
  if (!Number.isInteger(n) || n < 1) {
    return TIME_WINDOW_DEFAULT;
  }
  if (unit === "years" && n > ALL_TIME_MIN_YEARS) {
    return TIME_WINDOW_ALL_TIME;
  }
  return `last-${n}-${unit}`;
};

export const parseTimeWindowSelection = (
  value: unknown
): ParsedTimeWindowSelection | undefined => {
  if (typeof value !== "string" || value === "") {
    return undefined;
  }

  if ((NAMED_TIME_WINDOW_TOKENS as readonly string[]).includes(value)) {
    return { kind: "named", token: value as NamedTimeWindowToken };
  }

  const lastMatch = LAST_TOKEN_RE.exec(value);
  if (lastMatch) {
    const n = Number(lastMatch[1]);
    const unit = lastMatch[2] as DurationUnit;
    if (unit === "years" && n > ALL_TIME_MIN_YEARS) {
      return undefined;
    }
    return { kind: "duration", n, unit };
  }

  const range = urlTimeWindow.deserializeDateRangeStr(value);
  if (range) {
    return { kind: "range", start: range[0], end: range[1] };
  }

  return undefined;
};

export const isValidTimeWindowSelection = (value: unknown): value is string =>
  parseTimeWindowSelection(value) !== undefined;

/**
 * What the URL `timeWindow` param should be after sync.
 * Valid present value is kept. Missing (`null`) restores a valid stored token.
 * Present-but-invalid (minutes, garbage) always becomes the default week —
 * storage is not consulted, so an old `?timeWindow=10080` bookmark cannot
 * revive a stored year/all-time selection.
 */
export const resolveTimeWindowUrlParam = (
  raw: string | null,
  stored?: string
): string => {
  if (isValidTimeWindowSelection(raw)) {
    return raw;
  }
  if (raw == null && isValidTimeWindowSelection(stored)) {
    return stored;
  }
  return TIME_WINDOW_DEFAULT;
};

/** Map UI increment minutes to a persistable token. Not a URL migrate. */
export const durationMinutesToToken = (minutes: number): string => {
  const units = calculateTimeUnits(minutes);
  if (!units) {
    return TIME_WINDOW_DEFAULT;
  }
  if (units.years > ALL_TIME_MIN_YEARS) {
    return TIME_WINDOW_ALL_TIME;
  }
  if (units.years > 0) {
    return serializeDurationToken(units.years, "years");
  }
  if (units.months > 0) {
    return serializeDurationToken(units.months, "months");
  }
  if (units.days > 0) {
    return serializeDurationToken(units.days, "days");
  }
  if (units.hours > 0) {
    return serializeDurationToken(units.hours, "hours");
  }
  return serializeDurationToken(1, "hours");
};

const NAMED_LABELS: Record<NamedTimeWindowToken, string> = {
  yesterday: "Yesterday",
  "month-to-date": "Month to date",
  "previous-month": "Previous month",
  [TIME_WINDOW_ALL_TIME]: ALL_TIME_LABEL,
};

/** Tokens are always plural (a URL contract); labels are not. */
const readableUnit = (n: number, unit: DurationUnit): string =>
  n === 1 ? unit.slice(0, -1) : unit;

/** "7 days" / "1 hour" — reads inside a sentence ("in the past 7 days"). */
export const getTimeWindowDurationText = (selection: string): string | undefined => {
  const parsed = parseTimeWindowSelection(selection);

  return parsed?.kind === "duration"
    ? `${parsed.n} ${readableUnit(parsed.n, parsed.unit)}`
    : undefined;
};

export const getTimeWindowSelectionLabel = (selection: string): string => {
  const parsed = parseTimeWindowSelection(selection);
  if (!parsed) {
    return "Invalid date range";
  }
  if (parsed.kind === "named") {
    return NAMED_LABELS[parsed.token];
  }
  if (parsed.kind === "duration") {
    return `Last ${getTimeWindowDurationText(selection)}`;
  }
  return "";
};
